import styled from "styled-components";
import PanelHeader from "../../components/molecules/PanelHeader";
import { stageNames } from "../../lib/workflow";
import FilledButton from "../../components/atoms/FilledButton";
import { ReactComponent as ArrowRightIcon } from "../../assets/svg/arrow-right.svg";
import {
  StyledRadioContainer,
  StyledRadioInput,
  StyledRadioLabel,
} from "../../components/molecules/RadioButtons";
import { TextInput } from "../../components/atoms/TextInput";
import { SubHeading, UnitSpan } from "../../components/atoms/Text";
import HelpButton from "../../components/atoms/HelpButton";
import { IProps as DialogProps } from "../../components/organisms/Dialog";
import { useDialogContext } from "../../context/ContextProvider";
import { initialDialogProps } from "../Other/DialogContainer";
import { Link } from "../../components/atoms/Link";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import { formatNumberToFixed } from "../../utils/otherUtils";
import { assessorsHandbook, guidelines } from "../../lib/constants";

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled.div`
  overflow: auto;
  flex: 1;
  padding: 8px 12px;
  letter-spacing: -0.01875rem;
`;

const HelpButtonContainer = styled.span`
  display: inline-flex;
  margin-left: 16px;
`;

const StyledRadioGroupContainer = styled.div`
  margin-block: 12px;
`;

const InputsContainer = styled.div`
  margin-bottom: 52px;
  margin-left: 43px;
`;

const InputLabel = styled.p`
  margin-block: 16px 4px;
  letter-spacing: normal;
`;

const StyledInput = styled(TextInput)`
  width: min(198px, 90%);
`;

const FooterButton = styled(FilledButton)`
  transition: background-color 0.3s linear;
  height: 56px;
  border-radius: 0;
`;
const FooterArrowIcon = styled(ArrowRightIcon)`
  height: 14px;
  margin-left: 10px;
`;

const HelpContentContainer = styled.div`
  display: block;
  font-weight: 300;
`;

interface IProps {
  close: any;
  setStage: any;
  pastRemovalProps: {
    pastRemoval: boolean | null;
    pastRemovalPermitPinNumber: string;
    pastRemovalArea: string;
    setPastRemoval: any;
    setPastRemovalPermitPinNumber: any;
    setPastRemovalArea: any;
  };
  showFooter: boolean;
}

function PastRemoval({
  close,
  setStage,
  pastRemovalProps,
  showFooter,
}: IProps) {
  const { setDialogProps }: { setDialogProps: (props: DialogProps) => void } =
    useDialogContext();

  const onHelpClick = (title: string, helpContent: any) => {
    setDialogProps({
      open: true,
      onClose: () => {
        setDialogProps(initialDialogProps);
      },
      heading: title,
      children: helpContent,
      modal: false,
    });
  };

  const onRadioChange = (e: any) => {
    if (pastRemovalProps.pastRemoval === null) {
    }
    pastRemovalProps.setPastRemoval(e.target.value === "true");
  };

  const pastRemovalHelp = (
    <HelpContentContainer>
      <p>
        The past removal policy is detailed in Section 3.2.1 of the{" "}
        <Link
          href={guidelines}
          target="_blank"
          title="Guidelines for the removal, destruction or lopping of native vegetation"
          rel="noopener noreferrer"
        >
          Guidelines
        </Link>
        <ExternalLinkIcon /> and Appendix 3 of the{" "}
        <Link
          href={assessorsHandbook}
          target="_blank"
          title="Assessor’s Handbook"
          rel="noopener noreferrer"
        >
          Assessor’s Handbook
        </Link>
        <ExternalLinkIcon />.
      </p>
      <p>
        Past removal is considered in current clearing proposals to ensure
        cumulative impacts on biodiversity are accounted for when native
        vegetation removal occurring on a single property, or as part of a
        single project is subject to separate approvals.
      </p>
      <p style={{ fontWeight: 500 }}>
        You are required to provide the details of any past removal if:
      </p>
      <ul>
        <li>
          You have received approval to remove native vegetation on your
          property, or on contiguous land in your ownership, within the past
          five years;
        </li>
        <li>
          You have removed native vegetation without approval on your property,
          or on contiguous land in your ownership, within the past five years,
          or
        </li>
        <li>
          The current native vegetation removal forms part of a single project,
          noting that Appendix 2, Part E of the{" "}
          <Link
            href={assessorsHandbook}
            target="_blank"
            title="Assessor’s Handbook"
            rel="noopener noreferrer"
          >
            Assessor’s Handbook
          </Link>
          <ExternalLinkIcon /> provides guidance on defining a single project.
        </li>
      </ul>
      <p>
        Past removal does not include native vegetation removal that was subject
        to:
      </p>
      <ul>
        <li>An approval granted more than five years ago; or</li>
        <li>An exemption, where the Guidelines were not applied.</li>
      </ul>
      <p>
        If you are unsure if past removal applies to your proposal, you should
        contact the responsible authority (e.g. Local Council) to confirm
        whether any approvals to remove native vegetation have been issued for
        your property in the last five years, or if your current proposal is
        considered to form part of a broader single project.
      </p>
      <p>
        Accounting for past removal{" "}
        <span style={{ fontWeight: 500 }}>does not</span> increase the offset
        requirements for your current proposal. Past removal is only used to
        determine:
      </p>
      <ul>
        <li>The appropriate assessment pathway; and</li>
        <li>
          If previous and current proposals to remove native vegetation will
          have a proportionally significant impact on rare or threatened
          species.
        </li>
      </ul>
    </HelpContentContainer>
  );

  const permitPinHelp = (
    <HelpContentContainer>
      <p>
        If available, please provide the unique identifier of any relevant
        planning permits or Planning Infringement Notices (PINs).
      </p>
    </HelpContentContainer>
  );

  const removalAreaHelp = (
    <HelpContentContainer>
      <p>
        Please provide the area in hectares, to three decimal places where
        possible. The total area for each permit/PIN must be added together if
        more than one applies.
      </p>
    </HelpContentContainer>
  );

  return (
    <Container>
      <PanelHeader title={stageNames["pastRemoval"]} onClose={close} />
      <ContentContainer>
        <SubHeading>
          Do you need to account for past removal?
          <HelpButtonContainer>
            <HelpButton
              onClick={() => onHelpClick("Past removal", pastRemovalHelp)}
            />
          </HelpButtonContainer>
        </SubHeading>
        <StyledRadioGroupContainer>
          <StyledRadioContainer>
            <StyledRadioInput
              type="radio"
              id="pastRemovalYes"
              name="pastRemoval"
              value={"true"}
              onChange={onRadioChange}
              checked={pastRemovalProps.pastRemoval === true}
            />
            <StyledRadioLabel htmlFor={"pastRemovalYes"}>
              Yes - details of past removal to be provided below:
            </StyledRadioLabel>
          </StyledRadioContainer>

          <InputsContainer>
            <InputLabel>
              Permit/PIN number(s)
              <HelpButtonContainer>
                <HelpButton
                  onClick={() =>
                    onHelpClick("Permit/PIN number(s)", permitPinHelp)
                  }
                />
              </HelpButtonContainer>
            </InputLabel>
            <StyledInput
              maxLength={100}
              placeholder="Enter number (optional)"
              value={pastRemovalProps.pastRemovalPermitPinNumber}
              onChange={(e: any) =>
                pastRemovalProps.setPastRemovalPermitPinNumber(e.target.value)
              }
              disabled={pastRemovalProps.pastRemoval !== true}
            ></StyledInput>
            <InputLabel>
              Total area <UnitSpan>(ha)</UnitSpan>
              <HelpButtonContainer>
                <HelpButton
                  onClick={() => onHelpClick("Total area", removalAreaHelp)}
                />
              </HelpButtonContainer>
            </InputLabel>
            <StyledInput
              type="number"
              min={0.001}
              step={"0.001"}
              placeholder="Enter number"
              value={pastRemovalProps.pastRemovalArea}
              onChange={(e: any) =>
                pastRemovalProps.setPastRemovalArea(e.target.value)
              }
              onBlur={(e: any) =>
                pastRemovalProps.setPastRemovalArea(
                  formatNumberToFixed(Number(e.target.value), 3)
                )
              }
              disabled={pastRemovalProps.pastRemoval !== true}
            ></StyledInput>
          </InputsContainer>

          <StyledRadioContainer>
            <StyledRadioInput
              type="radio"
              id="pastRemovalNo"
              name="pastRemoval"
              value={"false"}
              onChange={onRadioChange}
              checked={pastRemovalProps.pastRemoval === false}
            />
            <StyledRadioLabel htmlFor={"pastRemovalNo"}>No</StyledRadioLabel>
          </StyledRadioContainer>
        </StyledRadioGroupContainer>
      </ContentContainer>
      {showFooter ? (
        <FooterButton onClick={() => setStage(1)}>
          <span>Map Native Vegetation</span>
          <FooterArrowIcon />
        </FooterButton>
      ) : null}
    </Container>
  );
}

export default PastRemoval;
